import { useState, useEffect, useCallback, useRef } from "react";
import {
  getMenu, createMenuCategory, updateMenuCategory, reorderMenuCategories, deleteMenuCategory,
  createMenuItem, updateMenuItem, reorderMenuItems, deleteMenuItem, downloadMenuStandPdf, analyzeMenuImport, applyMenuImport,
} from "@/api";
import { Loading } from "./Layout";
import { Input } from "./ui/input";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "./ui/alert-dialog";
import { UtensilsCrossed, Plus, ArrowUp, ArrowDown, Pencil, Trash2, Check, X, Eye, EyeOff, Download, Upload, Loader2, CheckCircle2, ChevronDown, Leaf } from "lucide-react";
import { toast } from "sonner";

function formatErr(detail) {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && e.msg) || JSON.stringify(e)).join(" ");
  return "Etwas ist schiefgelaufen.";
}

const eur = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" });
const fmtPrice = (cents) => eur.format((Number(cents) || 0) / 100);
const centsToInput = (cents) => ((Number(cents) || 0) / 100).toFixed(2).replace(".", ",");
const fmtBytes = (b) => {
  const n = Number(b) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1).replace(".", ",")} MB`;
};
const parsePriceToCents = (raw) => {
  const s = String(raw ?? "").replace(/€/g, "").replace(/\s/g, "").replace(",", ".").trim();
  if (s === "") return null;
  if (!/^\d*\.?\d*$/.test(s) || s === ".") return null;
  const n = Number(s);
  if (!isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
};

const ALLERGENS = [
  ["gluten", "Glutenhaltiges Getreide"], ["crustaceans", "Krebstiere"], ["eggs", "Eier"], ["fish", "Fisch"],
  ["peanuts", "Erdnüsse"], ["soy", "Soja"], ["milk", "Milch / Laktose"], ["nuts", "Schalenfrüchte"],
  ["celery", "Sellerie"], ["mustard", "Senf"], ["sesame", "Sesam"], ["sulphites", "Schwefeldioxid / Sulfite"],
  ["lupin", "Lupinen"], ["molluscs", "Weichtiere"],
];
const DIETARY = [[null, "Keine Angabe"], ["vegetarian", "Vegetarisch"], ["vegan", "Vegan"]];
const DIET_LABEL = { vegetarian: "Vegetarisch", vegan: "Vegan" };
const MAX_VARIANTS = 6;
const emptyVariant = () => ({ label: "", price: "" });

export const MenuTab = ({ business }) => {
  const feedbackOn = !!business?.modules?.feedback;
  const [cats, setCats] = useState(null);
  const [dlSize, setDlSize] = useState(null);
  const [items, setItems] = useState([]);
  const [error, setError] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [newNote, setNewNote] = useState("");
  const [confirmDelCat, setConfirmDelCat] = useState(null);
  const [confirmDelItem, setConfirmDelItem] = useState(null);
  const [busy, setBusy] = useState(false);
  const [imp, setImp] = useState({ state: "idle", res: null, error: "" });
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try { const d = await getMenu(); setCats(d.categories || []); setItems(d.items || []); }
    catch (e) { setError(true); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // ---- Category handlers ----
  const addCategory = async () => {
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    try {
      const cat = await createMenuCategory({ name, note: newNote.trim() });
      setCats((c) => [...c, cat]);
      setNewName(""); setNewNote(""); setAdding(false);
      toast.success("Kategorie hinzugefügt ✓");
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const renameCat = async (c, name, note) => {
    const nm = name.trim();
    if (!nm) return false;
    try {
      const upd = await updateMenuCategory(c.id, { name: nm, note: note.trim() });
      setCats((l) => l.map((x) => (x.id === c.id ? { ...x, name: upd.name, note: upd.note } : x)));
      toast.success("Gespeichert ✓");
      return true;
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); return false; }
  };
  const toggleCat = async (c) => {
    try {
      const upd = await updateMenuCategory(c.id, { active: !c.active });
      setCats((l) => l.map((x) => (x.id === c.id ? { ...x, active: upd.active } : x)));
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const moveCat = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= cats.length) return;
    const next = [...cats];
    [next[idx], next[j]] = [next[j], next[idx]];
    setCats(next);
    try { await reorderMenuCategories(next.map((c) => c.id)); }
    catch (e) { toast.error("Sortierung fehlgeschlagen"); load(); }
  };
  const doDeleteCat = async () => {
    const c = confirmDelCat;
    setBusy(true);
    try {
      await deleteMenuCategory(c.id);
      setCats((l) => l.filter((x) => x.id !== c.id));
      toast.success("Kategorie gelöscht");
      setConfirmDelCat(null);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); setConfirmDelCat(null); }
    finally { setBusy(false); }
  };

  // ---- Item handlers ----
  const addItem = async (categoryId, data) => {
    try {
      const it = await createMenuItem({ category_id: categoryId, ...data });
      setItems((l) => [...l, it]);
      toast.success("Produkt hinzugefügt ✓");
      return true;
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); return false; }
  };
  const saveItem = async (it, data) => {
    try {
      const upd = await updateMenuItem(it.id, data);
      if (data.category_id && data.category_id !== it.category_id) await load();
      else setItems((l) => l.map((x) => (x.id === it.id ? upd : x)));
      toast.success("Gespeichert ✓");
      return true;
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); return false; }
  };
  const toggleItem = async (it) => {
    try {
      const upd = await updateMenuItem(it.id, { active: !it.active });
      setItems((l) => l.map((x) => (x.id === it.id ? upd : x)));
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const moveItem = async (categoryId, idx, dir) => {
    const list = items.filter((i) => i.category_id === categoryId).sort((a, b) => a.sort_order - b.sort_order);
    const j = idx + dir;
    if (j < 0 || j >= list.length) return;
    [list[idx], list[j]] = [list[j], list[idx]];
    const order = list.map((i) => i.id);
    setItems((all) => all.map((i) => (i.category_id === categoryId ? { ...i, sort_order: order.indexOf(i.id) } : i)));
    try { await reorderMenuItems(categoryId, order); }
    catch (e) { toast.error("Sortierung fehlgeschlagen"); load(); }
  };
  const doDeleteItem = async () => {
    const it = confirmDelItem;
    setBusy(true);
    try {
      await deleteMenuItem(it.id);
      setItems((l) => l.filter((x) => x.id !== it.id));
      toast.success("Produkt gelöscht");
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setBusy(false); setConfirmDelItem(null); }
  };

  const downloadStand = async (size) => {
    if (dlSize) return;
    setDlSize(size);
    try {
      const blob = await downloadMenuStandPdf(size);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `speisekarte-aufsteller-${size}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Der Aufsteller konnte nicht erstellt werden."); }
    finally { setDlSize(null); }
  };

  const onPickImport = async (e) => {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = "";
    if (!file) return;
    setImp({ state: "uploading", res: null, error: "" });
    try {
      const res = await analyzeMenuImport(file);
      setImp({ state: "done", res, error: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail
        : (err?.response ? "Die Datei konnte nicht geprüft werden." : "Netzwerkfehler. Bitte versuche es erneut.");
      setImp({ state: "error", res: null, error: msg });
    }
  };
  const resetImport = () => setImp({ state: "idle", res: null, error: "" });

  if (error) return (
    <div data-testid="menu-error" className="max-w-2xl mx-auto bg-white rounded-3xl ring-1 ring-slate-100 shadow-soft p-10 text-center">
      <p className="text-slate-500 text-sm font-medium">Die Menükarte konnte nicht geladen werden. Bitte versuche es später erneut.</p>
    </div>
  );
  if (!cats) return <Loading />;

  return (
    <div data-testid="menu-tab" className="max-w-2xl mx-auto space-y-4">
      <div className="bg-white rounded-3xl ring-1 ring-slate-100 shadow-soft p-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="font-display font-extrabold text-lg text-[var(--bs-ink)]">Menükarte</h2>
            <p className="text-sm text-slate-500 leading-relaxed mt-1">Organisiere deine Menükarte in Kategorien.</p>
          </div>
          {!adding && (
            <button data-testid="menu-add-category-toggle" onClick={() => setAdding(true)}
              className="inline-flex items-center gap-2 rounded-xl bg-[var(--bs-teal)] hover:bg-[var(--bs-teal-hover)] text-white px-4 py-2.5 text-sm font-bold press whitespace-nowrap shrink-0">
              <Plus className="h-4 w-4" /> Kategorie hinzufügen
            </button>
          )}
        </div>
        {adding && (
          <div className="mt-4 space-y-2.5">
          <div className="flex flex-col sm:flex-row gap-2.5">
            <Input data-testid="menu-new-category-input" autoFocus value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") addCategory(); if (e.key === "Escape") { setAdding(false); setNewName(""); setNewNote(""); } }}
              placeholder="z. B. Getränke" className="h-11 rounded-xl flex-1" />
            <div className="flex gap-2.5">
              <button data-testid="menu-new-category-submit" onClick={addCategory} disabled={busy || !newName.trim()}
                className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[var(--bs-teal)] hover:bg-[var(--bs-teal-hover)] text-white font-bold press disabled:opacity-60">
                <Check className="h-4 w-4" /> Hinzufügen
              </button>
              <button data-testid="menu-new-category-cancel" onClick={() => { setAdding(false); setNewName(""); setNewNote(""); }}
                className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-white ring-1 ring-slate-200 hover:bg-slate-50 text-slate-600 font-bold press">
                <X className="h-4 w-4" /> Abbrechen
              </button>
            </div>
          </div>
            <Input data-testid="menu-new-category-note-input" value={newNote} maxLength={200}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Hinweis (optional), z. B. Brunch 9–14 Uhr · Wochenende bis 15 Uhr" className="h-10 rounded-xl text-sm" />
          </div>
        )}
      </div>

      {/* Speisekarte importieren (AI import — Erkennung folgt) */}
      <div data-testid="menu-import-section" className="bg-white rounded-3xl ring-1 ring-slate-100 shadow-soft p-6">
        <div className="flex items-center gap-2">
          <Upload className="h-4 w-4 text-[var(--bs-teal)]" />
          <h3 className="font-display font-extrabold text-base text-[var(--bs-ink)]">Menükarte importieren</h3>
        </div>
        <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">
          Lade eine bestehende Menükarte als PDF oder Foto hoch. Bonstelo erstellt daraus später einen Entwurf, den du vor dem Übernehmen prüfen kannst.
        </p>

        <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={onPickImport} className="hidden" data-testid="menu-import-input" />

        {imp.state === "idle" && (
          <div className="mt-4">
            <button data-testid="menu-import-pick" onClick={() => fileRef.current?.click()}
              className="inline-flex items-center gap-2 rounded-xl bg-white ring-1 ring-slate-200 hover:bg-slate-50 text-slate-700 px-4 py-2.5 text-sm font-bold press">
              <Upload className="h-4 w-4" /> PDF oder Foto auswählen
            </button>
            <p className="text-xs font-medium text-slate-400 mt-2">PDF, JPG oder PNG · maximal 10 MB · PDF maximal 10 Seiten</p>
          </div>
        )}

        {imp.state === "uploading" && (
          <div data-testid="menu-import-loading" className="mt-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-600">
              <Loader2 className="h-4 w-4 animate-spin" /> Menükarte wird erkannt …
            </div>
            <p className="text-xs font-medium text-slate-400 mt-1">Das kann einen Moment dauern.</p>
          </div>
        )}

        {imp.state === "done" && imp.res && (
          <ImportDraftEditor res={imp.res} onDiscard={resetImport} onApplied={async (r) => {
            await load();
            resetImport();
            toast.success(`${r.categories_created} Kategorien und ${r.items_created} Produkte hinzugefügt ✓`);
          }} />
        )}

        {imp.state === "error" && (
          <div data-testid="menu-import-error" className="mt-4">
            <p className="text-sm font-semibold text-rose-600">{imp.error}</p>
            <button data-testid="menu-import-retry" onClick={resetImport}
              className="mt-3 inline-flex items-center gap-2 rounded-xl bg-white ring-1 ring-slate-200 hover:bg-slate-50 text-slate-700 px-4 py-2.5 text-sm font-bold press">
              <Upload className="h-4 w-4" /> Andere Datei auswählen
            </button>
          </div>
        )}
      </div>

      {/* QR-Aufsteller */}
      <div data-testid="menu-stand-section" className="bg-white rounded-3xl ring-1 ring-slate-100 shadow-soft p-6">
        <div className="flex items-center gap-2">
          <Download className="h-4 w-4 text-[var(--bs-teal)]" />
          <h3 className="font-display font-extrabold text-base text-[var(--bs-ink)]">QR-Aufsteller</h3>
        </div>
        <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">
          {feedbackOn
            ? "Ein Aufsteller für Menükarte und Feedback – Vorderseite Menükarte, Rückseite Feedback."
            : "Gäste können deine Menükarte direkt per QR-Code öffnen."}
        </p>
        <p data-testid="menu-stand-status" className="text-xs font-semibold text-slate-400 mt-1">
          {feedbackOn
            ? "Feedback ist aktiviert – die PDF wird doppelseitig erstellt."
            : "Die PDF enthält nur die Menükarten-Seite."}
        </p>
        <div className="flex flex-wrap gap-2.5 mt-4">
          {["a6", "a5", "a4"].map((sz) => (
            <button key={sz} data-testid={`menu-stand-${sz}`} onClick={() => downloadStand(sz)} disabled={!!dlSize}
              className="inline-flex items-center gap-2 rounded-xl bg-white ring-1 ring-slate-200 hover:bg-slate-50 text-slate-700 px-4 py-2.5 text-sm font-bold press disabled:opacity-60">
              <Download className="h-4 w-4" /> {dlSize === sz ? "Wird erstellt …" : `${sz.toUpperCase()} herunterladen`}
            </button>
          ))}
        </div>
      </div>

      {cats.length === 0 ? (
        <div data-testid="menu-empty" className="bg-white rounded-3xl ring-1 ring-slate-100 shadow-soft p-10 text-center">
          <div className="mx-auto h-14 w-14 rounded-2xl bg-slate-100 flex items-center justify-center mb-3">
            <UtensilsCrossed className="h-7 w-7 text-slate-400" />
          </div>
          <h3 className="font-display font-extrabold text-xl text-[var(--bs-ink)]">Noch keine Kategorien vorhanden.</h3>
          <p className="text-slate-500 text-sm font-medium mt-2 leading-relaxed max-w-sm mx-auto">
            Erstelle deine erste Kategorie, zum Beispiel „Getränke“ oder „Hauptgerichte“.
          </p>
        </div>
      ) : (
        <div data-testid="menu-category-list" className="space-y-4">
          {cats.map((c, idx) => (
            <CategoryBlock
              key={c.id}
              cat={c}
              cats={cats}
              catItems={items.filter((i) => i.category_id === c.id).sort((a, b) => a.sort_order - b.sort_order)}
              isFirst={idx === 0}
              isLast={idx === cats.length - 1}
              busy={busy}
              onMoveCat={(dir) => moveCat(idx, dir)}
              onRenameCat={renameCat}
              onToggleCat={toggleCat}
              onDeleteCat={() => setConfirmDelCat(c)}
              onAddItem={addItem}
              onSaveItem={saveItem}
              onToggleItem={toggleItem}
              onMoveItem={moveItem}
              onDeleteItem={(it) => setConfirmDelItem(it)}
            />
          ))}
        </div>
      )}

      <AlertDialog open={!!confirmDelCat} onOpenChange={(o) => !o && setConfirmDelCat(null)}>
        <AlertDialogContent data-testid="menu-confirm-delete">
          <AlertDialogHeader>
            <AlertDialogTitle>Kategorie löschen?</AlertDialogTitle>
            <AlertDialogDescription>„{confirmDelCat?.name}“ wird dauerhaft gelöscht.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction data-testid="menu-confirm-delete-btn" disabled={busy} onClick={doDeleteCat}
              className="bg-rose-600 hover:bg-rose-700">Löschen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!confirmDelItem} onOpenChange={(o) => !o && setConfirmDelItem(null)}>
        <AlertDialogContent data-testid="menu-confirm-delete-item">
          <AlertDialogHeader>
            <AlertDialogTitle>Produkt löschen?</AlertDialogTitle>
            <AlertDialogDescription>„{confirmDelItem?.name}“ wird dauerhaft gelöscht.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction data-testid="menu-confirm-delete-item-btn" disabled={busy} onClick={doDeleteItem}
              className="bg-rose-600 hover:bg-rose-700">Löschen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const IconBtn = ({ testid, title, onClick, disabled, children, className = "" }) => (
  <button data-testid={testid} title={title} onClick={onClick} disabled={disabled}
    className={`p-2 rounded-lg press disabled:opacity-30 disabled:hover:bg-transparent ${className}`}>
    {children}
  </button>
);

const hasExtras = (it) => !!(it && (it.note || (it.price_variants || []).length || it.dietary || (it.allergens || []).length));

const VariantRows = ({ rows, setRows }) => {
  const upd = (i, patch) => setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <div data-testid="menu-variant-rows" className="space-y-2">
      {rows.map((r, i) => (
        <div key={i} className="grid grid-cols-[minmax(0,1fr)_92px_auto] gap-2 items-center">
          <Input data-testid={`menu-variant-label-${i}`} value={r.label} maxLength={30} onChange={(e) => upd(i, { label: e.target.value })} placeholder="Bezeichnung, z. B. klein" className="h-10 rounded-xl" />
          <Input data-testid={`menu-variant-price-${i}`} value={r.price} onChange={(e) => upd(i, { price: e.target.value })} placeholder="Preis" inputMode="decimal" className="h-10 rounded-xl" />
          <IconBtn testid={`menu-variant-remove-${i}`} title="Entfernen" disabled={rows.length <= 1} onClick={() => setRows(rows.filter((_, j) => j !== i))} className="text-slate-400 hover:bg-slate-100"><X className="h-4 w-4" /></IconBtn>
        </div>
      ))}
      {rows.length < MAX_VARIANTS && (
        <button type="button" data-testid="menu-variant-add" onClick={() => setRows([...rows, emptyVariant()])}
          className="inline-flex items-center gap-1.5 text-xs font-bold text-[var(--bs-teal)] hover:opacity-80 press">
          <Plus className="h-3.5 w-3.5" /> Variante hinzufügen
        </button>
      )}
    </div>
  );
};

const Chip = ({ on, onClick, testid, children }) => (
  <button type="button" data-testid={testid} aria-pressed={on} onClick={onClick}
    className={`px-3 py-1.5 rounded-full text-xs font-bold ring-1 transition-colors duration-150 press ${on ? "bg-[var(--bs-teal)] text-white ring-[var(--bs-teal)]" : "bg-white text-slate-600 ring-slate-200 hover:bg-slate-50"}`}>
    {children}
  </button>
);

const ItemExtras = ({ s, set, allergenHint = "Bitte nur Allergene angeben, die du sicher geprüft hast." }) => (
  <div data-testid="menu-item-extras" className="space-y-4 pt-1">
    <div className="space-y-2">
      <label className="flex items-center gap-2 text-xs font-bold text-slate-500 cursor-pointer">
        <input type="checkbox" data-testid="menu-variants-toggle" checked={s.useVariants} onChange={(e) => set.toggleVariants(e.target.checked)} className="h-4 w-4 accent-[var(--bs-teal)]" />
        Mehrere Preise / Größen
      </label>
      {s.useVariants && <VariantRows rows={s.variants} setRows={set.variants} />}
    </div>
    <div className="space-y-1.5">
      <label className="text-xs font-bold text-slate-500">Hinweis</label>
      <Input data-testid="menu-item-note-input" value={s.note} maxLength={200} onChange={(e) => set.note(e.target.value)} placeholder="z. B. Glutenfreies Brot +1,50 €" className="h-10 rounded-xl" />
    </div>
    <div className="space-y-1.5">
      <label className="text-xs font-bold text-slate-500">Ernährung</label>
      <div className="flex flex-wrap gap-1.5">
        {DIETARY.map(([v, l]) => <Chip key={l} testid={`menu-dietary-${v || "none"}`} on={s.dietary === v} onClick={() => set.dietary(v)}>{l}</Chip>)}
      </div>
    </div>
    <div className="space-y-1.5">
      <label className="text-xs font-bold text-slate-500">Allergene</label>
      <p className="text-[11px] text-slate-400 font-medium">{allergenHint}</p>
      <div className="flex flex-wrap gap-1.5">
        {ALLERGENS.map(([id, l]) => (
          <Chip key={id} testid={`menu-allergen-${id}`} on={s.allergens.includes(id)}
            onClick={() => set.allergens(s.allergens.includes(id) ? s.allergens.filter((a) => a !== id) : [...s.allergens, id])}>{l}</Chip>
        ))}
      </div>
    </div>
  </div>
);

const ItemForm = ({ initial, onSubmit, onCancel, cats, showCategory = false, submitLabel }) => {
  const [name, setName] = useState(initial?.name || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [price, setPrice] = useState(initial ? centsToInput(initial.price_cents) : "");
  const [categoryId, setCategoryId] = useState(initial?.category_id || (cats[0] && cats[0].id));
  const initVariants = initial?.price_variants || [];
  const [moreOpen, setMoreOpen] = useState(hasExtras(initial));
  const [useVariants, setUseVariants] = useState(initVariants.length > 0);
  const [variants, setVariants] = useState(initVariants.length ? initVariants.map((v) => ({ label: v.label, price: centsToInput(v.price_cents) })) : [emptyVariant(), emptyVariant()]);
  const [note, setNote] = useState(initial?.note || "");
  const [dietary, setDietary] = useState(initial?.dietary || null);
  const [allergens, setAllergens] = useState(initial?.allergens || []);

  const toggleVariants = (on) => {
    setUseVariants(on);
    if (!on && initVariants.length) setPrice("");
  };
  const variantCents = variants.map((v) => parsePriceToCents(v.price)).filter((c) => c !== null);
  const lowest = variantCents.length ? Math.min(...variantCents) : null;

  const submit = () => {
    if (!name.trim()) { toast.error("Bitte einen Produktnamen eingeben."); return; }
    const data = { name: name.trim(), description: description.trim(), note: note.trim(), dietary, allergens };
    if (useVariants) {
      const rows = variants.map((v) => ({ label: v.label.trim(), price_cents: parsePriceToCents(v.price) }));
      if (rows.some((r) => !r.label || r.price_cents === null)) { toast.error("Bitte für jede Variante Bezeichnung und gültigen Preis angeben."); return; }
      if (new Set(rows.map((r) => r.label.toLowerCase())).size !== rows.length) { toast.error("Jede Variante braucht eine eigene Bezeichnung."); return; }
      data.price_variants = rows;
      data.price_cents = Math.min(...rows.map((r) => r.price_cents));
    } else {
      const cents = parsePriceToCents(price);
      if (cents === null) { toast.error("Bitte einen gültigen Preis eingeben (z. B. 3,90)."); return; }
      data.price_cents = cents;
      data.price_variants = [];
    }
    if (showCategory) data.category_id = categoryId;
    onSubmit(data);
  };

  return (
    <div className="space-y-2.5">
      <div className="space-y-1.5">
        <label className="text-xs font-bold text-slate-500">Produktname</label>
        <Input data-testid="menu-item-name-input" autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="z. B. Cappuccino" className="h-10 rounded-xl" />
      </div>
      <div className="space-y-1.5">
        <label className="text-xs font-bold text-slate-500">Beschreibung</label>
        <Input data-testid="menu-item-desc-input" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional" className="h-10 rounded-xl" />
      </div>
      <div className="grid grid-cols-2 gap-2.5">
        <div className="space-y-1.5">
          <label className="text-xs font-bold text-slate-500">Preis</label>
          {useVariants ? (
            <div data-testid="menu-item-price-from" className="h-10 rounded-xl bg-slate-50 ring-1 ring-slate-200 px-3 flex items-center text-sm font-bold text-slate-600 tabular-nums">
              {lowest === null ? "–" : `Ab ${fmtPrice(lowest)}`}
            </div>
          ) : (
            <Input data-testid="menu-item-price-input" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="z. B. 3,90" inputMode="decimal" className="h-10 rounded-xl" />
          )}
        </div>
        {showCategory && (
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-slate-500">Kategorie</label>
            <select data-testid="menu-item-category-select" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}
              className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-[var(--bs-teal)]">
              {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        )}
      </div>
      <div>
        <button type="button" data-testid="menu-item-more-toggle" aria-expanded={moreOpen} onClick={() => setMoreOpen((o) => !o)}
          className="inline-flex items-center gap-1 text-xs font-bold text-slate-500 hover:text-slate-700 press py-1">
          Weitere Angaben <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${moreOpen ? "rotate-180" : ""}`} />
        </button>
        {moreOpen && (
          <ItemExtras s={{ useVariants, variants, note, dietary, allergens }}
            set={{ toggleVariants, variants: setVariants, note: setNote, dietary: setDietary, allergens: setAllergens }} />
        )}
      </div>
      <div className="flex gap-2.5 pt-1">
        <button data-testid="menu-item-submit" onClick={submit}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[var(--bs-teal)] hover:bg-[var(--bs-teal-hover)] text-white text-sm font-bold press">
          <Check className="h-4 w-4" /> {submitLabel}
        </button>
        <button data-testid="menu-item-cancel" onClick={onCancel}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white ring-1 ring-slate-200 hover:bg-slate-50 text-slate-600 text-sm font-bold press">
          <X className="h-4 w-4" /> Abbrechen
        </button>
      </div>
    </div>
  );
};

const CategoryBlock = ({ cat, cats, catItems, isFirst, isLast, busy,
  onMoveCat, onRenameCat, onToggleCat, onDeleteCat,
  onAddItem, onSaveItem, onToggleItem, onMoveItem, onDeleteItem }) => {
  const [renaming, setRenaming] = useState(false);
  const [renameVal, setRenameVal] = useState(cat.name);
  const [noteVal, setNoteVal] = useState(cat.note || "");
  const [addingItem, setAddingItem] = useState(false);
  const [editId, setEditId] = useState(null);

  const submitRename = async () => { if (await onRenameCat(cat, renameVal, noteVal)) setRenaming(false); };

  return (
    <div data-testid="menu-category-block" data-active={cat.active} className="bg-white rounded-3xl ring-1 ring-slate-100 shadow-soft overflow-hidden">
      {/* Category header */}
      <div className="flex items-center gap-2 px-4 sm:px-5 py-3.5 border-b border-slate-100 bg-slate-50/60">
        <div className="flex flex-col shrink-0">
          <IconBtn testid="menu-move-up" title="Nach oben" disabled={isFirst} onClick={() => onMoveCat(-1)} className="text-slate-400 hover:bg-slate-100"><ArrowUp className="h-4 w-4" /></IconBtn>
          <IconBtn testid="menu-move-down" title="Nach unten" disabled={isLast} onClick={() => onMoveCat(1)} className="text-slate-400 hover:bg-slate-100"><ArrowDown className="h-4 w-4" /></IconBtn>
        </div>
        <div className="flex-1 min-w-0">
          {renaming ? (
            <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Input data-testid="menu-edit-input" autoFocus value={renameVal} onChange={(e) => setRenameVal(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") submitRename(); if (e.key === "Escape") setRenaming(false); }} className="h-9 rounded-lg" />
              <button data-testid="menu-edit-save" onClick={submitRename} disabled={busy || !renameVal.trim()}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-[var(--bs-teal)] hover:bg-[var(--bs-teal-hover)] text-white text-xs font-bold press disabled:opacity-60"><Check className="h-3.5 w-3.5" /> Speichern</button>
              <button data-testid="menu-edit-cancel" onClick={() => setRenaming(false)}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-slate-500 hover:bg-slate-100 text-xs font-bold press"><X className="h-3.5 w-3.5" /> Abbrechen</button>
            </div>
              <Input data-testid="menu-edit-note-input" value={noteVal} maxLength={200} onChange={(e) => setNoteVal(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") submitRename(); if (e.key === "Escape") setRenaming(false); }}
                placeholder="Hinweis (optional), z. B. Brunch 9–14 Uhr" className="h-9 rounded-lg text-sm" />
            </div>
          ) : (
            <>
            <p className="font-display font-extrabold text-slate-900 text-base flex items-center gap-2 truncate">
              {cat.name}
              <span data-testid="menu-category-status" className={`text-[10px] font-bold rounded-full px-2 py-0.5 shrink-0 ${cat.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"}`}>{cat.active ? "Aktiv" : "Inaktiv"}</span>
            </p>
            {cat.note && <p data-testid="menu-category-note" className="text-xs text-slate-500 truncate mt-0.5">{cat.note}</p>}
            </>
          )}
        </div>
        {!renaming && (
          <div className="flex items-center gap-0.5 shrink-0">
            <IconBtn testid="menu-edit-toggle" title="Bearbeiten" onClick={() => { setRenameVal(cat.name); setNoteVal(cat.note || ""); setRenaming(true); }} className="text-slate-400 hover:bg-slate-100"><Pencil className="h-4 w-4" /></IconBtn>
            <IconBtn testid="menu-toggle-active" title={cat.active ? "Deaktivieren" : "Aktivieren"} onClick={() => onToggleCat(cat)} className={cat.active ? "text-amber-600 hover:bg-amber-50" : "text-emerald-600 hover:bg-emerald-50"}>{cat.active ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</IconBtn>
            <IconBtn testid="menu-delete" title="Löschen" onClick={onDeleteCat} className="text-rose-500 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></IconBtn>
          </div>
        )}
      </div>

      {/* Products */}
      {catItems.length === 0 ? (
        <p data-testid="menu-items-empty" className="text-sm text-slate-400 font-medium px-5 py-4">Noch keine Produkte in dieser Kategorie.</p>
      ) : (
        <div className="divide-y divide-slate-100">
          {catItems.map((it, i) => (
            editId === it.id ? (
              <div key={it.id} className="px-4 sm:px-5 py-4 bg-slate-50/40">
                <ItemForm initial={it} cats={cats} showCategory submitLabel="Speichern"
                  onCancel={() => setEditId(null)}
                  onSubmit={async (data) => { if (await onSaveItem(it, data)) setEditId(null); }} />
              </div>
            ) : (
              <div key={it.id} data-testid="menu-item-row" data-active={it.active} className="flex items-center gap-2 px-4 sm:px-5 py-3.5">
                <div className="flex flex-col shrink-0">
                  <IconBtn testid="menu-item-move-up" title="Nach oben" disabled={i === 0} onClick={() => onMoveItem(cat.id, i, -1)} className="text-slate-400 hover:bg-slate-100"><ArrowUp className="h-4 w-4" /></IconBtn>
                  <IconBtn testid="menu-item-move-down" title="Nach unten" disabled={i === catItems.length - 1} onClick={() => onMoveItem(cat.id, i, 1)} className="text-slate-400 hover:bg-slate-100"><ArrowDown className="h-4 w-4" /></IconBtn>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-display font-bold text-slate-900 text-sm truncate">{it.name}</p>
                    <span data-testid="menu-item-status" className={`text-[10px] font-bold rounded-full px-2 py-0.5 shrink-0 ${it.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"}`}>{it.active ? "Aktiv" : "Inaktiv"}</span>
                  </div>
                  {it.description && <p className="text-xs text-slate-500 mt-0.5 leading-relaxed line-clamp-2">{it.description}</p>}
                  {DIET_LABEL[it.dietary] && (
                    <span data-testid="menu-item-dietary" className="inline-flex items-center gap-1 mt-1 text-[10px] font-bold text-emerald-700"><Leaf className="h-3 w-3" />{DIET_LABEL[it.dietary]}</span>
                  )}
                </div>
                <span data-testid="menu-item-price" className="font-display font-bold text-slate-900 text-sm whitespace-nowrap shrink-0 tabular-nums">{(it.price_variants || []).length ? `ab ${fmtPrice(it.price_cents)}` : fmtPrice(it.price_cents)}</span>
                <div className="flex items-center gap-0.5 shrink-0">
                  <IconBtn testid="menu-item-edit" title="Bearbeiten" onClick={() => setEditId(it.id)} className="text-slate-400 hover:bg-slate-100"><Pencil className="h-4 w-4" /></IconBtn>
                  <IconBtn testid="menu-item-toggle-active" title={it.active ? "Deaktivieren" : "Aktivieren"} onClick={() => onToggleItem(it)} className={it.active ? "text-amber-600 hover:bg-amber-50" : "text-emerald-600 hover:bg-emerald-50"}>{it.active ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</IconBtn>
                  <IconBtn testid="menu-item-delete" title="Löschen" onClick={() => onDeleteItem(it)} className="text-rose-500 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></IconBtn>
                </div>
              </div>
            )
          ))}
        </div>
      )}

      {/* Add product */}
      <div className="px-4 sm:px-5 py-3.5 border-t border-slate-100">
        {addingItem ? (
          <ItemForm cats={cats} submitLabel="Hinzufügen"
            onCancel={() => setAddingItem(false)}
            onSubmit={async (data) => { if (await onAddItem(cat.id, data)) setAddingItem(false); }} />
        ) : (
          <button data-testid="menu-add-item-toggle" onClick={() => setAddingItem(true)}
            className="inline-flex items-center gap-2 text-sm font-bold text-[var(--bs-teal)] hover:opacity-80 press">
            <Plus className="h-4 w-4" /> Produkt hinzufügen
          </button>
        )}
      </div>
    </div>
  );
};

let _localSeq = 0;
const localId = (p) => `draft_${p}_local_${Date.now()}_${++_localSeq}`;

const variantsInvalid = (it) => it.useVariants && (it.variants.length === 0 ||
  it.variants.some((v) => !v.label.trim() || parsePriceToCents(v.price) === null) ||
  new Set(it.variants.map((v) => v.label.trim().toLowerCase())).size !== it.variants.length);
const itemInvalid = (it) => !it.name.trim() || (it.useVariants ? variantsInvalid(it) : parsePriceToCents(it.price) === null);
const needsReview = (it) => itemInvalid(it) || it.uncertain;

const ImportDraftEditor = ({ res, onDiscard, onApplied }) => {
  const [cats, setCats] = useState(() =>
    (res.categories || []).map((c) => ({
      temp_id: c.temp_id,
      name: c.name,
      note: c.note || "",
      items: (c.items || []).map((it) => {
        const pv = it.price_variants || [];
        return {
          temp_id: it.temp_id,
          name: it.name,
          description: it.description || "",
          price: it.price_cents == null ? "" : centsToInput(it.price_cents),
          uncertain: !!it.uncertain,
          note: it.note || "",
          dietary: it.dietary || null,
          allergens: it.allergens || [],
          useVariants: pv.length > 0,
          variants: pv.length ? pv.map((v) => ({ label: v.label || "", price: v.price_cents == null ? "" : centsToInput(v.price_cents) })) : [emptyVariant(), emptyVariant()],
          moreOpen: hasExtras(it),
        };
      }),
    }))
  );
  const [dirty, setDirty] = useState(false);
  const [onlyReview, setOnlyReview] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [applyOpen, setApplyOpen] = useState(false);
  const [applying, setApplying] = useState(false);

  const mutate = (fn) => { setDirty(true); setCats(fn); };

  const setCat = (ci, patch) => mutate((cs) => cs.map((c, i) => (i === ci ? { ...c, ...patch } : c)));
  const setItem = (ci, ii, patch) => mutate((cs) => cs.map((c, i) => {
    if (i !== ci) return c;
    return { ...c, items: c.items.map((it, j) => (j === ii ? { ...it, ...patch } : it)) };
  }));
  const onPriceChange = (ci, ii, val) => {
    const cents = parsePriceToCents(val);
    setItem(ci, ii, cents !== null ? { price: val, uncertain: false } : { price: val });
  };
  const onVariantsChange = (ci, ii, it, rows) => {
    const next = { ...it, variants: rows };
    setItem(ci, ii, variantsInvalid(next) ? { variants: rows } : { variants: rows, uncertain: false });
  };
  const addCategory = () => mutate((cs) => [...cs, { temp_id: localId("cat"), name: "", note: "", items: [] }]);
  const removeCategory = (ci) => mutate((cs) => cs.filter((_, i) => i !== ci));
  const addItem = (ci) => mutate((cs) => cs.map((c, i) => (i === ci
    ? { ...c, items: [...c.items, { temp_id: localId("item"), name: "", description: "", price: "", uncertain: true,
      note: "", dietary: null, allergens: [], useVariants: false, variants: [emptyVariant(), emptyVariant()], moreOpen: false }] } : c)));
  const removeItem = (ci, ii) => mutate((cs) => cs.map((c, i) => (i === ci ? { ...c, items: c.items.filter((_, j) => j !== ii) } : c)));
  const moveCat = (ci, dir) => mutate((cs) => {
    const j = ci + dir; if (j < 0 || j >= cs.length) return cs;
    const n = [...cs]; [n[ci], n[j]] = [n[j], n[ci]]; return n;
  });
  const moveItem = (ci, ii, dir) => mutate((cs) => cs.map((c, i) => {
    if (i !== ci) return c;
    const j = ii + dir; if (j < 0 || j >= c.items.length) return c;
    const it = [...c.items]; [it[ii], it[j]] = [it[j], it[ii]]; return { ...c, items: it };
  }));
  const moveItemToCat = (ci, ii, destTempId) => mutate((cs) => {
    const di = cs.findIndex((c) => c.temp_id === destTempId);
    if (di === -1 || di === ci) return cs;
    const item = cs[ci].items[ii];
    return cs.map((c, i) => {
      if (i === ci) return { ...c, items: c.items.filter((_, j) => j !== ii) };
      if (i === di) return { ...c, items: [...c.items, item] };
      return c;
    });
  });

  const totalItems = cats.reduce((n, c) => n + c.items.length, 0);
  const reviewCount = cats.reduce((n, c) => n + c.items.filter(needsReview).length, 0);
  const invalid = cats.some((c) => !c.name.trim()) || cats.some((c) => c.items.some(itemInvalid));

  const askDiscard = () => {
    if (!dirty) { onDiscard(); return; }
    setConfirm({ title: "Entwurf verwerfen?", desc: "Deine Änderungen an diesem Import-Entwurf gehen verloren.",
      action: "Verwerfen", onOk: onDiscard });
  };
  const askRemoveCat = (ci) => {
    const c = cats[ci];
    if (c.items.length === 0) { removeCategory(ci); return; }
    setConfirm({ title: "Kategorie aus Entwurf entfernen?",
      desc: "Die Kategorie und alle darin enthaltenen Produkte werden nur aus diesem Import-Entwurf entfernt.",
      action: "Entfernen", onOk: () => removeCategory(ci) });
  };
  const askRemoveItem = (ci, ii) => {
    const nm = cats[ci].items[ii].name || "Produkt";
    setConfirm({ title: "Produkt aus Entwurf entfernen?", desc: `„${nm}“ wird nur aus diesem Import-Entwurf entfernt.`,
      action: "Entfernen", onOk: () => removeItem(ci, ii) });
  };

  const doApply = async () => {
    setApplying(true);
    try {
      const payload = {
        categories: cats.map((c) => ({
          name: c.name.trim(),
          note: c.note.trim(),
          items: c.items.map((it) => {
            const pv = it.useVariants ? it.variants.map((v) => ({ label: v.label.trim(), price_cents: parsePriceToCents(v.price) })) : [];
            return {
              name: it.name.trim(),
              description: it.description.trim(),
              price_cents: pv.length ? Math.min(...pv.map((v) => v.price_cents)) : parsePriceToCents(it.price),
              price_variants: pv,
              note: it.note.trim(),
              dietary: it.dietary,
              allergens: it.allergens,
            };
          }),
        })),
      };
      const r = await applyMenuImport(payload);
      setApplyOpen(false);
      toast.success("Import abgeschlossen ✓");
      onApplied(r);
    } catch (e) {
      setApplyOpen(false);
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Der Import konnte nicht übernommen werden. Bitte versuche es erneut.");
    } finally {
      setApplying(false);
    }
  };

  const inputCls = "h-9 rounded-lg text-sm";

  return (
    <div data-testid="menu-import-editor" className="mt-4">
      <div className="flex items-center gap-2 text-sm font-bold text-emerald-600">
        <CheckCircle2 className="h-4 w-4" /> Menükarte erkannt
      </div>
      <p className="text-xs font-medium text-slate-500 mt-1.5" data-testid="menu-import-source">
        {res.source?.filename}
        {res.source?.page_count != null ? ` · ${res.source.page_count} ${res.source.page_count === 1 ? "Seite" : "Seiten"}` : ""}
        {` · ${fmtBytes(res.source?.size_bytes)}`}
      </p>

      <div className="mt-3 rounded-2xl bg-amber-50 ring-1 ring-amber-100 px-4 py-3">
        <p className="text-xs font-semibold text-amber-700 leading-relaxed">
          Bitte prüfe und korrigiere den Entwurf. Noch wird nichts in deiner Menükarte gespeichert.
        </p>
      </div>

      <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
        <p data-testid="menu-import-summary" className="text-xs font-bold text-slate-600">
          {cats.length} {cats.length === 1 ? "Kategorie" : "Kategorien"} · {totalItems} {totalItems === 1 ? "Produkt" : "Produkte"}
          {reviewCount > 0 && <span className="text-amber-600"> · {reviewCount} {reviewCount === 1 ? "Eintrag prüfen" : "Einträge prüfen"}</span>}
        </p>
        {reviewCount > 0 && (
          <button data-testid="menu-import-filter" onClick={() => setOnlyReview((v) => !v)}
            className={`text-[11px] font-bold rounded-full px-2.5 py-1 press ring-1 ${onlyReview ? "bg-amber-500 text-white ring-transparent" : "bg-white text-slate-500 ring-slate-200"}`}>
            Nur zu prüfende Einträge
          </button>
        )}
      </div>

      {res.warnings?.length > 0 && (
        <div data-testid="menu-import-warnings" className="mt-3 rounded-2xl bg-slate-50 ring-1 ring-slate-100 px-4 py-3">
          <p className="text-xs font-bold text-slate-600">Hinweise zur Erkennung</p>
          <ul className="mt-1.5 space-y-1">
            {res.warnings.map((w, i) => <li key={i} className="text-xs font-medium text-slate-500 leading-relaxed">• {w}</li>)}
          </ul>
        </div>
      )}

      <div data-testid="menu-import-draft" className="mt-4 space-y-3">
        {cats.map((c, ci) => {
          const visItems = c.items.map((it, ii) => ({ it, ii })).filter(({ it }) => !onlyReview || needsReview(it));
          if (onlyReview && visItems.length === 0 && c.name.trim()) return null;
          return (
            <div key={c.temp_id} data-testid="menu-import-cat" className="rounded-2xl ring-1 ring-slate-200 bg-white overflow-hidden">
              <div className="flex items-center gap-2 px-3 py-2.5 bg-slate-50/70 border-b border-slate-100">
                <div className="flex flex-col shrink-0">
                  <button title="Nach oben" disabled={ci === 0} onClick={() => moveCat(ci, -1)} className="p-0.5 rounded text-slate-400 hover:bg-slate-100 disabled:opacity-30"><ArrowUp className="h-3.5 w-3.5" /></button>
                  <button title="Nach unten" disabled={ci === cats.length - 1} onClick={() => moveCat(ci, 1)} className="p-0.5 rounded text-slate-400 hover:bg-slate-100 disabled:opacity-30"><ArrowDown className="h-3.5 w-3.5" /></button>
                </div>
                <Input data-testid="menu-import-cat-name" value={c.name} onChange={(e) => setCat(ci, { name: e.target.value })}
                  placeholder="Kategoriename" className={`${inputCls} font-bold flex-1 ${!c.name.trim() ? "ring-2 ring-rose-300" : ""}`} />
                <button data-testid="menu-import-cat-remove" title="Kategorie entfernen" onClick={() => askRemoveCat(ci)} className="p-1.5 rounded-lg text-rose-500 hover:bg-rose-50 press shrink-0"><Trash2 className="h-4 w-4" /></button>
              </div>
              <div className="px-3 pt-2.5">
                <Input data-testid="menu-import-cat-note" value={c.note} maxLength={200} onChange={(e) => setCat(ci, { note: e.target.value })}
                  placeholder="Hinweis zur Kategorie (optional)" className={`${inputCls} text-xs`} />
              </div>
              <div className="p-3 space-y-2.5">
                {visItems.length === 0 ? (
                  <p className="text-xs text-slate-400 font-medium">{onlyReview ? "Keine zu prüfenden Einträge." : "Noch keine Produkte."}</p>
                ) : visItems.map(({ it, ii }) => {
                  const priceInvalid = parsePriceToCents(it.price) === null;
                  const vc = it.variants.map((v) => parsePriceToCents(v.price)).filter((x) => x !== null);
                  return (
                    <div key={it.temp_id} data-testid="menu-import-item" className="rounded-xl ring-1 ring-slate-100 p-2.5">
                      <div className="flex items-start gap-2">
                        <div className="flex flex-col shrink-0 pt-1">
                          <button title="Nach oben" disabled={ii === 0} onClick={() => moveItem(ci, ii, -1)} className="p-0.5 rounded text-slate-300 hover:bg-slate-100 disabled:opacity-30"><ArrowUp className="h-3.5 w-3.5" /></button>
                          <button title="Nach unten" disabled={ii === c.items.length - 1} onClick={() => moveItem(ci, ii, 1)} className="p-0.5 rounded text-slate-300 hover:bg-slate-100 disabled:opacity-30"><ArrowDown className="h-3.5 w-3.5" /></button>
                        </div>
                        <div className="flex-1 min-w-0 space-y-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <Input data-testid="menu-import-item-name" value={it.name} onChange={(e) => setItem(ci, ii, { name: e.target.value })}
                              placeholder="Produktname" className={`${inputCls} font-semibold flex-1 ${!it.name.trim() ? "ring-2 ring-rose-300" : ""}`} />
                            {needsReview(it) && <span data-testid="menu-import-uncertain" className="text-[10px] font-bold rounded-full px-2 py-0.5 bg-amber-100 text-amber-700 shrink-0 whitespace-nowrap">Bitte prüfen</span>}
                          </div>
                          <Input data-testid="menu-import-item-desc" value={it.description} onChange={(e) => setItem(ci, ii, { description: e.target.value.slice(0, 500) })}
                            placeholder="Beschreibung (optional)" className={inputCls} />
                          <div className="flex flex-wrap items-center gap-2">
                            {it.useVariants ? (
                              <div data-testid="menu-import-item-price-from" className={`h-9 w-28 rounded-lg bg-slate-50 ring-1 px-2.5 flex items-center text-xs font-bold text-slate-600 ${variantsInvalid(it) ? "ring-amber-300 ring-2" : "ring-slate-200"}`}>
                                {vc.length ? `Ab ${fmtPrice(Math.min(...vc))}` : "–"}
                              </div>
                            ) : (
                            <Input data-testid="menu-import-item-price" value={it.price} inputMode="decimal" onChange={(e) => onPriceChange(ci, ii, e.target.value)}
                              placeholder="Preis eingeben" className={`${inputCls} w-28 ${priceInvalid ? "ring-2 ring-amber-300" : ""}`} />
                            )}
                            <select data-testid="menu-import-item-move" value={c.temp_id} onChange={(e) => moveItemToCat(ci, ii, e.target.value)}
                              className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-xs font-medium text-slate-600 flex-1 min-w-0 basis-24">
                              {cats.map((cc) => <option key={cc.temp_id} value={cc.temp_id}>{cc.name || "Ohne Namen"}</option>)}
                            </select>
                            <button data-testid="menu-import-item-remove" title="Produkt entfernen" onClick={() => askRemoveItem(ci, ii)} className="p-1.5 rounded-lg text-rose-500 hover:bg-rose-50 press shrink-0"><Trash2 className="h-4 w-4" /></button>
                          </div>
                          <button type="button" data-testid="menu-import-item-more" aria-expanded={it.moreOpen} onClick={() => setCats((cs) => cs.map((cc, i) => (i !== ci ? cc : { ...cc, items: cc.items.map((x, j) => (j === ii ? { ...x, moreOpen: !x.moreOpen } : x)) })))}
                            className="inline-flex items-center gap-1 text-xs font-bold text-slate-500 hover:text-slate-700 press py-0.5">
                            Weitere Angaben <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${it.moreOpen ? "rotate-180" : ""}`} />
                          </button>
                          {it.moreOpen && (
                            <ItemExtras allergenHint="Bitte nur Allergene übernehmen, die du sicher geprüft hast."
                              s={it}
                              set={{
                                toggleVariants: (on) => setItem(ci, ii, { useVariants: on }),
                                variants: (rows) => onVariantsChange(ci, ii, it, rows),
                                note: (v) => setItem(ci, ii, { note: v }),
                                dietary: (v) => setItem(ci, ii, { dietary: v }),
                                allergens: (v) => setItem(ci, ii, { allergens: v }),
                              }} />
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <button data-testid="menu-import-add-item" onClick={() => addItem(ci)} className="inline-flex items-center gap-1.5 text-xs font-bold text-[var(--bs-teal)] hover:opacity-80 press">
                  <Plus className="h-3.5 w-3.5" /> Produkt hinzufügen
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <button data-testid="menu-import-add-cat" onClick={addCategory} className="mt-3 inline-flex items-center gap-2 rounded-xl bg-white ring-1 ring-slate-200 hover:bg-slate-50 text-slate-700 px-4 py-2.5 text-sm font-bold press">
        <Plus className="h-4 w-4" /> Kategorie hinzufügen
      </button>

      <p data-testid="menu-import-status" className={`mt-4 text-sm font-bold ${invalid ? "text-amber-600" : "text-emerald-600"}`}>
        {invalid ? "Bitte prüfe noch markierte Felder." : "Entwurf vollständig geprüft."}
      </p>

      {!invalid && totalItems > 0 && (
        <div data-testid="menu-import-apply-summary" className="mt-3 rounded-2xl bg-emerald-50 ring-1 ring-emerald-100 px-4 py-3">
          <p className="text-sm font-bold text-emerald-800">
            {cats.length} {cats.length === 1 ? "Kategorie" : "Kategorien"} und {totalItems} {totalItems === 1 ? "Produkt" : "Produkte"} werden zu deiner Menükarte hinzugefügt.
          </p>
          <p className="text-xs font-medium text-emerald-700 mt-1 leading-relaxed">
            Bestehende Einträge bleiben unverändert. Die neuen Einträge sind nach dem Übernehmen direkt in deiner öffentlichen Menükarte sichtbar.
          </p>
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button data-testid="menu-import-apply" disabled={invalid || totalItems === 0 || applying} onClick={() => setApplyOpen(true)}
          className="inline-flex items-center gap-2 rounded-xl bg-[var(--bs-teal)] hover:bg-[var(--bs-teal-hover)] text-white px-5 py-2.5 text-sm font-bold press disabled:opacity-50 disabled:cursor-not-allowed">
          <Check className="h-4 w-4" /> Übernehmen
        </button>
        <button data-testid="menu-import-reset" onClick={askDiscard} disabled={applying} className="inline-flex items-center gap-2 text-sm font-bold text-slate-500 hover:text-slate-700 press disabled:opacity-50">
          <X className="h-4 w-4" /> Andere Datei auswählen
        </button>
      </div>

      <AlertDialog open={applyOpen} onOpenChange={(o) => { if (!applying) setApplyOpen(o); }}>
        <AlertDialogContent data-testid="menu-import-apply-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Import übernehmen?</AlertDialogTitle>
            <AlertDialogDescription>
              {cats.length} {cats.length === 1 ? "Kategorie" : "Kategorien"} und {totalItems} {totalItems === 1 ? "Produkt" : "Produkte"} werden zu deiner bestehenden Menükarte hinzugefügt.
              {" "}Bestehende Einträge bleiben unverändert. Die neuen Einträge sind anschließend direkt in deiner öffentlichen Menükarte sichtbar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={applying}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction data-testid="menu-import-apply-ok" disabled={applying}
              onClick={(e) => { e.preventDefault(); doApply(); }}
              className="bg-[var(--bs-teal)] hover:bg-[var(--bs-teal-hover)]">
              {applying ? "Menükarte wird übernommen …" : "Jetzt übernehmen"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent data-testid="menu-import-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm?.title}</AlertDialogTitle>
            <AlertDialogDescription>{confirm?.desc}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction data-testid="menu-import-confirm-ok" onClick={() => { const ok = confirm?.onOk; setConfirm(null); ok && ok(); }} className="bg-rose-600 hover:bg-rose-700">{confirm?.action}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
